<template>
    <div style="padding-right: 1px">

        <div class="text-right">
            <v-btn v-if="$vuetify.breakpoint.mobile" color="primary" @click="hideEditPanel()" text>{{$translate('Select component on page')}}<v-icon right>mdi-arrow-right</v-icon></v-btn>
        </div>

        <v-tabs v-if="config"
                fixed-tabs
                icons-and-text
                v-model="currentOpenSettingsPanel"
                color="zoezi-edit-color"
                class="z-no-arrows"
        >
            <v-tab>
                {{brokenComponentTitle}}<v-icon>{{componentIcon}}</v-icon>
            </v-tab>

            <v-tab>
                {{$translate('Content')}}<v-icon>mdi-cog</v-icon>
            </v-tab>

            <v-tab>
                {{$translate('Advanced')}}<v-icon>mdi-radioactive</v-icon>
            </v-tab>

            <v-tab-item class="pa-4">
                <div v-if="isPage">
                    <page-template-palette v-model="pageTemplate" @input="pageTemplateChanged()" ref="pagetemplatepalette" show-remove />

                    <v-btn v-if="isSuperUser" @click="showSavePageTemplateDialog = true" color="zoezi-edit-color">{{$translate('Save page as a template')}}</v-btn>

                    <v-dialog max-width="500" v-model="showSavePageTemplateDialog">
                        <v-card>
                            <v-card-title class="headline zoezi-edit-color">{{$translate('Save template')}}</v-card-title>

                            <v-card-text class="pt-4">
                                <v-text-field :label="$translate('Name')" v-model="newPageTemplateName" color="zoezi-edit-color"/>
                                <icon-setting :label="$translate('Icon')" v-model="newPageTemplateIcon" />
                            </v-card-text>

                            <v-card-actions>
                                <v-spacer></v-spacer>

                                <v-btn
                                    color="zoezi-edit-color"
                                    :disabled="!newPageTemplateValid"
                                    @click="savePageAsPageTemplate"
                                >
                                    {{$translate('Save')}}
                                </v-btn>

                            </v-card-actions>
                        </v-card>

                    </v-dialog>
                </div>
                <component-palette v-else :component="component" :key="paletteKey" />
            </v-tab-item>

            <v-tab-item class="pa-4">
                <div :key="config ? (config.id + '_' + config.component) : undefined">
                    <h2 v-if="cprops.length == 0" class="secondary--text">{{$translate('The component lacks settings')}}</h2>
                    <component-props-setting :config="config" @save="delayedSave" />

                    <div class="ml-4" v-if="wrapper && wrapper.canRemove">
                        <v-btn fab color="error" @click="remove">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>

                    <div v-if="parentComponent">
                        <label class="v-label theme--light">{{$translate('Parent component')}}</label>
                        <div @click="$store.commit('setEditComponent', parentComponent)" class="black--text" style="cursor: pointer">
                            <div style="line-height: 40px">
                                <v-icon>{{parentComponent.$options.zoezi.icon}}</v-icon>
                                {{$translate(parentComponent.$options.zoezi.title)}}
                            </div>
                        </div>
                    </div>

                    <vue-code-setting v-if="ownComponent" :add="false" :named="true" :value="ownComponent" @input="saveOwnComponent" />

                    <v-btn v-if="!$store.state.editMenu" color="zoezi-edit-color" @click="showComponentTree = true" class="mt-2">{{$translate('Show component tree')}}</v-btn>

                    <div class="d-flex mt-4" v-if="canBeCopied">
                        <v-btn color="zoezi-edit-color" @click="copyComponent">
                            {{$translate('Copy')}}
                            <v-chip style="text-transform: none" small class="ml-2">
                                <v-icon class="mr-1">{{componentIcon}}</v-icon>
                                {{componentTitle}}
                            </v-chip>
                        </v-btn>
                    </div>

                    <div class="d-flex mt-4" v-if="canBeCopied">
                        <v-btn color="zoezi-edit-color" @click="pasteComponent" :disabled="!copiedComponent">
                            {{$translate('Paste')}}
                            <v-chip style="text-transform: none" small v-if="copiedComponent" class="ml-2">
                                <v-icon class="mr-1">{{copiedComponent.zoezi.icon}}</v-icon>
                                {{$translate(copiedComponent.zoezi.title)}}
                            </v-chip>
                        </v-btn>
                        <div class="ml-2 black--text d-flex align-center" v-if="copiedComponent">{{$translate('Replaces')}} <v-icon class="ml-2 mr-1">{{componentIcon}}</v-icon>{{componentTitle}}</div>
                        <div class="ml-2 black--text d-flex align-center" v-else><v-icon class="mr-1">mdi-clipboard-alert-outline</v-icon>{{$translate('Nothing in clipboard')}}</div>
                    </div>
                </div>
            </v-tab-item>

            <v-tab-item class="pa-4">
                <div v-if="config && config.style" :key="config.id">
                    <margin-padding-setting :label="$translate('Margin')" @input="setMargins" :value="{left: config.style.marginLeft, right: config.style.marginRight, top: config.style.marginTop, bottom: config.style.marginBottom}" />
                    <margin-padding-setting :label="$translate('Padding')" @input="setPaddings" :value="{left: config.style.paddingLeft, right: config.style.paddingRight, top: config.style.paddingTop, bottom: config.style.paddingBottom}" />

                    <color-setting v-model="config.style.backgroundColor" :label="$translate('Background color')" @input="delayedSave"/>
                    <color-setting v-model="config.style.color" :label="$translate('Text color')" @input="delayedSave"/>

                    <alignment-setting v-model="config.style.textAlign" :label="$translate('Text alignment')" @input="save" />

                    <visibility-setting v-model="config.style.visible" :label="$translate('Visibility')"  @input="delayedSave" />

                    <v-switch :label="$translate('Fill vertically')" v-model="config.style.fillHeight" @change="delayedSave" color="zoezi-edit-color"></v-switch>

                    <v-switch :label="$translate('Show only when scrolled into view')" v-model="config.style.lazy" @change="delayedSave" color="zoezi-edit-color"></v-switch>

                    <animation-setting :label="$translate('Animation')" v-model="config.style.animation" @input="save" seekers entrances />

                    <v-text-field v-model="config.style.classes" :label="$translate('CSS classes')" @input="delayedSave" color="zoezi-edit-color" />

                    <page-params-setting :label="$translate('Page parameters')" v-model="config.style.pageParams" :props="cprops" @input="savePageParams" />

                    <component-template-settings :label="$translate('Template settings')" :config="config" :props="cprops" />
                </div>
            </v-tab-item>
        </v-tabs>

        <div v-else class="pa-4">
            <h2 v-if="!$vuetify.breakpoint.mobile" class="black--text">{{$translate('Click on a component on the page to edit its content here')}}</h2>
        </div>

        <toolbox-window max-width="800px" v-if="showComponentTree" scrollable :label="$translate('Component tree')" close @close="showComponentTree = false" center>
            <component-tree :min-height="500" max-height="90vh" :show-header="false" :show-close="false" @select="$store.commit('setEditComponent', $event)" />
        </toolbox-window>
    </div>
</template>

<script>
import components from '../component.js';
import ComponentPalette from './ComponentPalette.vue'
import PageTemplatePalette from './PageTemplatePalette.vue'
import MarginPaddingSetting from './MarginPaddingSetting.vue'
import ComponentTree from './ComponentTree.vue'
import VisibilitySetting from './VisibilitySetting.vue'
import AnimationSetting from './AnimationSetting.vue'
import ColorSetting from './ColorSetting.vue'
import IconSetting from './IconSetting.vue'
import AlignmentSetting from './AlignmentSetting.vue'
import VueCodeSetting from './VueCodeSetting.vue'
import PageParamsSetting from './PageParamsSetting.vue'
import ComponentTemplateSettings from './ComponentTemplateSettings.vue'
import ToolboxWindow from './ToolboxWindow.vue'
import ComponentPropsSetting from './ComponentPropsSetting.vue';

import {forEach, isObject, cloneDeep, debounce} from 'lodash';
export default {
    name: 'ComponentSettings',
    components: {
        ComponentPalette,
        ColorSetting,
        PageTemplatePalette,
        AlignmentSetting,
        MarginPaddingSetting,
        ComponentTree,
        VisibilitySetting,
        AnimationSetting,
        VueCodeSetting,
        PageParamsSetting,
        ComponentTemplateSettings,
        ToolboxWindow,
        IconSetting,
        ComponentPropsSetting
    },
    computed: {
        cprops() {
            return this.config ? components.getComponentProps(this.config.component) : [];
        },
        visibleProps() {
            return this.cprops.filter(prop => {
                if (!prop.typeinfo?.visible) {
                    return true;
                }

                let vprop = prop.typeinfo?.visible;
                let neg = vprop[0] == '!';
                if (neg) {
                    vprop = vprop.substring(1);
                }
                let value = this.config.props[vprop]
                return neg ? !value : !!value;
            });
        },
        propSections() {
            let sections = [];
            this.visibleProps.filter(x => x.section).forEach(prop => {
                let section = sections.find(sx => sx.name == prop.section);
                if (!section) {
                    section = {name: prop.section, props: []}
                    sections.push(section);
                }
                section.props.push(prop);
            });
            return sections;
        },
        isPage() {
            return this.component && this.component.$options.name == 'zoezi-page';
        },
        thisComponentInfo() {
            return this.config ? components.getComponent(this.config.component) : null;
        },
        parentComponent() {
            let v = this.component?.$parent;
            const cc = components.getAllComponents();
            while (v && !cc.find(x => x.name == v.$options.name)){
                v = v.$parent;
            }

            return v;
        },
        parentComponentConfig() {
            if (this.parentComponent) {
                return this.parentComponent.$parent.config;
            }

            return null;
        },
        config() {
            return this.$store.state.editComponentConfig;
        },
        component() {
            return this.$store.state.editComponent;
        },
        wrapper() {
            return this.component && this.component.$parent;
        },
        isSuperUser() {
            return this.$store.state.isLoggedIn && this.$store.state.user && this.$store.state.user.superUser;
        },
        newPageTemplateValid() {
            return this.newPageTemplateName && this.newPageTemplateName.length && this.newPageTemplateIcon;
        },
        componentTitle() {
            return this.$translate(this.thisComponentInfo?.zoezi?.title || '');
        },
        componentIcon() {
            return this.thisComponentInfo?.zoezi?.icon || 'mdi-sheep';
        },
        brokenComponentTitle() {
            let c = this.componentTitle;
            if (c == 'Gruppträningsschema') {
                return 'Grupp- tränings- schema';
            }

            return c;
        },
        ownComponent() {
            let name = this.component?.$options.name;
            return this.component && this.$root.siteconfig?.ownComponents?.[name];
        },
        canBeCopied() {
            if(this.thisComponentInfo?.name == 'zoezi-root' || this.thisComponentInfo?.name == 'zoezi-page') {
                return false;
            }
            return true;
        },
        copiedComponent() {
            if (this.$store.state.copiedComponent) {
                return components.getComponent(this.$store.state.copiedComponent.component);
            }

            return null;
        }
    },
    watch: {
        config() {
            this.setPanel();
        },
        component() {
            this.setPanel();
        }
    },
    data: () => ({
        currentOpenSettingsPanel: 0,
        paletteKey: 0,
        pageTemplate: {},
        showSavePageTemplateDialog: false,
        newPageTemplateName: '',
        newPageTemplateIcon: null,
        showComponentTree: false,
        savePending: false
    }),
    methods: {
        setPanel() {
            if (this.config) {
                if (!this.config.style) {
                    this.$set(this.config, 'style', {marginLeft: undefined, marginRight: undefined, marginTop: undefined, marginBottom: undefined, paddingLeft: undefined, paddingRight: undefined, paddingTop: undefined, paddingBottom: undefined});
                }

                if (this.config.component && this.config.component != 'zoezi-empty') {
                    this.currentOpenSettingsPanel = 1;
                } else {
                    this.currentOpenSettingsPanel = 0;
                }

                this.cprops.forEach(prop => {
                    if (prop.default !== undefined && this.config.props[prop.name] === undefined && !prop.global) {
                        this.config.props[prop.name] = typeof(prop.default) === 'function' ? prop.default() : prop.default;
                    }
                })
            }
            ++this.paletteKey;
        },
        save() {
            if (this.wrapper && this.wrapper.save) {
                this.wrapper.save();
            } else {
                this.$root.saveHomePage();
            }
        },
        remove() {
            this.wrapper.remove();
            this.$store.commit('setEditComponent', null);
        },
        saveNow() {
            this.debouncedSave.cancel();
            this.save();
        },
        saveNowIfNeeded() {
            this.debouncedSave.flush();
        },
        delayedSave() {
            this.debouncedSave();
            if (this.wrapper && this.wrapper.update) {
                this.wrapper.update();
            }
        },
        pageTemplateChanged() {
            let config = this.$root.siteconfig.routes.find(x => x.props.path == this.$route.path).props.config;
            Object.assign(config, this.pageTemplate.config);
            this.component.save();
        },
        setMargins(value) {
            let style = this.config.style;
            style.marginLeft = value.left;
            style.marginRight = value.right;
            style.marginTop = value.top;
            style.marginBottom = value.bottom;
            this.delayedSave();
        },
        setPaddings(value) {
            let style = this.config.style;
            style.paddingLeft = value.left;
            style.paddingRight = value.right;
            style.paddingTop = value.top;
            style.paddingBottom = value.bottom;
            this.delayedSave();
        },
        hideEditPanel() {
            this.$root.showEditPanel = false;
        },
        savePageAsPageTemplate() {
            let config = this.$root.siteconfig.routes.find(x => x.props.path == this.$route.path);
            config = config.props.config;
            config = cloneDeep(config);
            let removeIds = function(o) {
                forEach(o, (value, key) => {
                    if (isObject(value)) {
                        removeIds(value);
                    } else if (key == 'id' && o.component) {
                        delete o.id;
                    }
                });
            }
            removeIds(config);
            delete config.path;
            delete config.style;

            let name = this.newPageTemplateName;
            let icon = this.newPageTemplateIcon;
            let data = { config, name, icon };
            this.$api.post('/api/homepage/pagetemplate/add', data).then(() => {
                this.$refs.pagetemplatepalette.getTemplates();
                this.showSavePageTemplateDialog = false;
            });
        },
        saveOwnComponent(c) {
            if (!c.name) {
                return;
            }
            this.$root.siteconfig.ownComponents[c.id] = c;
            this.$root.saveHomePage();
            components.updateOwnComponents(this.$root.siteconfig);
            components.updateAllLiveComponents(c.id);
        },
        savePageParams(pageParams) {
            this.$set(this.config.style, 'pageParams', pageParams);
            this.save();
        },
        copyComponent() {
            let copy = cloneDeep(this.config);
            this.$store.commit('copyComponent', copy);
        },
        pasteComponent() {
            let c = cloneDeep(this.$store.state.copiedComponent);
            let createIds = function(o) {
                if (typeof o !== 'object' || !o) {
                    return;
                }

                if (o.component && typeof(o.component) === 'string' && o.id) {
                    o.id = components.makeEmptyConfig().id;
                }

                let arr = Array.isArray(o) ? o : Object.values(o);
                arr.forEach(x => {
                    createIds(x);
                })
            };

            createIds(c);
            Object.assign(this.config, c);
            this.$root.saveHomePage();
        }
    },
    mounted() {
        this.setPanel();
        this.debouncedSave = debounce(() => this.save(), 1000);
        window.addEventListener('beforeunload', this.saveNowIfNeeded);
    },
    destroyed() {
        window.removeEventListener('beforeunload', this.saveNowIfNeeded);
    }
}
</script>
